import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Trophy,
  User,
  Star,
  Gamepad2,
  Medal,
  Clock,
  ChevronRight,
  Crown,
  LogOut,
  Edit,
  CheckCircle,
  AlertCircle,
  Calendar,
  Target,
  Gift,
  LogIn,
  UserPlus,
  Play,
  Lock,
  Unlock,
  ArrowRight,
  ArrowLeft,
  Timer,
  RefreshCw,
  HelpCircle,
  X,
  Lightbulb,
  Sparkles,
  Loader2,
  Camera,
  Upload,
  Eye,
  EyeOff
} from 'lucide-react';
import api from '../../lib/axios';
import BankVault from '../../components/game/BankVault';
import { toast } from 'sonner';
import AnnualChestGame from '../../components/game/AnnualChestGame';

// Matrix Rain Effect Component - Casa de Papel Theme
const MatrixRain = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Characters for the Matrix effect - combination style
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789€$£¥₿#@&%?!';
    const charArray = chars.split('');
    
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    
    // Array to track the y position of drops
    const drops = [];
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * -100;
    }
    
    const draw = () => {
      // Slightly transparent black to create trail effect
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        // Random character
        const char = charArray[Math.floor(Math.random() * charArray.length)];
        
        // Alternate between red and dark red for depth
        const brightness = Math.random();
        if (brightness > 0.95) {
          ctx.fillStyle = '#ffffff'; // Occasional white flash
        } else if (brightness > 0.7) {
          ctx.fillStyle = '#ff3333'; // Bright red
        } else {
          ctx.fillStyle = '#8b0000'; // Dark red
        }
        
        // Draw the character
        ctx.fillText(char, i * fontSize, drops[i] * fontSize);
        
        // Reset drop randomly or when it goes off screen
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };
    
    const interval = setInterval(draw, 50);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0 opacity-20"
      style={{ mixBlendMode: 'screen' }}
    />
  );
};

const MONTHS = [
  { num: 1, name: 'Jan', fullName: 'Janvier' },
  { num: 2, name: 'Fév', fullName: 'Février' },
  { num: 3, name: 'Mar', fullName: 'Mars' },
  { num: 4, name: 'Avr', fullName: 'Avril' },
  { num: 5, name: 'Mai', fullName: 'Mai' },
  { num: 6, name: 'Juin', fullName: 'Juin' },
  { num: 7, name: 'Juil', fullName: 'Juillet' },
  { num: 8, name: 'Août', fullName: 'Août' },
  { num: 9, name: 'Sep', fullName: 'Septembre' },
  { num: 10, name: 'Oct', fullName: 'Octobre' },
  { num: 11, name: 'Nov', fullName: 'Novembre' },
  { num: 12, name: 'Déc', fullName: 'Décembre' }
];

// Hint Popup Modal Component - Casa de Papel Theme
const HintPopupModal = ({ show, monthNum, hint, onClose }) => {
  if (!show || !monthNum) return null;
  
  const monthInfo = MONTHS.find(m => m.num === monthNum);
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-gradient-to-br from-black via-red-950/50 to-black border-2 border-red-500/50 rounded-2xl p-6 max-w-md mx-4 shadow-2xl shadow-red-500/20 relative animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-900/50 hover:bg-red-800 flex items-center justify-center text-white/70 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center shadow-lg shadow-red-500/30">
            <Lightbulb className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white">
            Indice de {monthInfo?.fullName}
          </h3>
          <p className="text-sm text-red-400 mt-1">
            Caractère mystère #{monthNum}
          </p>
        </div>
        
        {/* Hint content */}
        {hint?.hint ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex-shrink-0 rounded-full bg-red-500/20 flex items-center justify-center">
                <HelpCircle className="w-4 h-4 text-red-400" />
              </div>
              <div>
                <p className="text-white font-medium">« {hint.hint} »</p>
                {hint.hint_detail && (
                  <p className="text-sm text-red-300/70 mt-2">{hint.hint_detail}</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-black/50 border border-red-500/30 rounded-xl p-4 mb-4 text-center">
            <p className="text-white/70">Aucun indice disponible pour ce mois.</p>
            <p className="text-xs text-red-300/50 mt-1">Suivez-nous sur les réseaux sociaux pour les indices !</p>
          </div>
        )}
        
        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-red-300/50">
            💡 Les indices sont publiés sur nos réseaux sociaux
          </p>
          <Button 
            onClick={onClose}
            className="mt-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white"
          >
            Compris !
          </Button>
        </div>
      </div>
    </div>
  );
};

const PlayerDashboardPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [player, setPlayer] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [allGames, setAllGames] = useState([]);
  const [mainGame, setMainGame] = useState(null);
  const [playedGameIds, setPlayedGameIds] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Leaderboard state
  const [annualLeaderboard, setAnnualLeaderboard] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  
  // Form states
  const [showLogin, setShowLogin] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerData, setRegisterData] = useState({
    email: '', password: '', confirmPassword: '', first_name: '',
    company_name: '', phone: '', pseudo: '', accepted_rules: false
  });
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerAvatar, setRegisterAvatar] = useState(null);
  const [registerAvatarPreview, setRegisterAvatarPreview] = useState(null);
  const registerAvatarInputRef = useRef(null);
  
  // Password visibility
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);
  
  // Pseudo editing
  const [editingPseudo, setEditingPseudo] = useState(false);
  const [newPseudo, setNewPseudo] = useState('');
  const [savingPseudo, setSavingPseudo] = useState(false);
  
  // Avatar upload
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const avatarInputRef = useRef(null);
  
  // Manual character entry for chest combination
  const [manualCharacters, setManualCharacters] = useState({});
  const [savingCharacter, setSavingCharacter] = useState(null);
  
  // Hint popup for monthly character
  const [showHintPopup, setShowHintPopup] = useState(false);
  const [selectedHintMonth, setSelectedHintMonth] = useState(null);
  const [selectedHintData, setSelectedHintData] = useState(null);
  
  // Annual game (Le Coffre MSC)
  const [annualGame, setAnnualGame] = useState(null);
  const [annualProgress, setAnnualProgress] = useState(null);
  
  // Handle avatar upload
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 2 Mo)');
      return;
    }
    
    const token = localStorage.getItem('game_token');
    if (!token) {
      toast.error('Vous devez être connecté');
      return;
    }
    
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const res = await api.post(`/game/players/avatar?token=${token}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update player data with new avatar
      setPlayer(prev => ({ ...prev, avatar_url: res.data.avatar_url }));
      toast.success('Avatar mis à jour !');
      setShowAvatarModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploadingAvatar(false);
    }
  };
  
  // Function to open hint popup
  const openHintPopup = (month, hint) => {
    setSelectedHintMonth(month);
    setSelectedHintData(hint);
    setShowHintPopup(true);
  };
  
  const closeHintPopup = () => {
    setShowHintPopup(false);
    setSelectedHintMonth(null);
    setSelectedHintData(null);
  };

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentDay = new Date().getDate();
  
  // Check if we're in the manual entry period (Dec 1-24)
  // Manual entry is available all year - last secret revealed on Dec 24
  const isManualEntryPeriod = true;

  useEffect(() => {
    fetchAllData();
    fetchAnnualLeaderboard();
  }, []);
  
  // Fetch annual leaderboard
  const fetchAnnualLeaderboard = async () => {
    setLoadingLeaderboard(true);
    try {
      const res = await api.get(`/game/leaderboard/annual/${currentYear}?limit=20`);
      setAnnualLeaderboard(res.data || []);
    } catch (e) {
      console.error('Error fetching leaderboard:', e);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  const fetchAllData = async () => {
    try {
      // Fetch games
      const [gamesRes, mainRes, annualRes] = await Promise.all([
        api.get('/game/contests'),
        api.get('/game/main-game'),
        api.get(`/game/annual-game/${currentYear}`).catch(() => ({ data: null }))
      ]);
      setAllGames(gamesRes.data || []);
      setMainGame(mainRes.data);
      setAnnualGame(annualRes.data);

      // Check if logged in
      const token = localStorage.getItem('game_token');
      if (token) {
        try {
          const [dashRes, progressRes] = await Promise.all([
            api.get(`/game/players/me/dashboard?token=${token}`),
            api.get(`/game/annual-game/${currentYear}/my-progress?token=${token}`).catch(() => ({ data: null }))
          ]);
          setDashboard(dashRes.data);
          setPlayer(dashRes.data.player);
          setNewPseudo(dashRes.data.player?.pseudo || '');
          setIsAuthenticated(true);
          setAnnualProgress(progressRes.data);
          
          // Get played game IDs from scores
          const playedIds = dashRes.data.recent_scores?.map(s => s.game_id) || [];
          setPlayedGameIds(playedIds);
        } catch (err) {
          if (err.response?.status === 401) {
            localStorage.removeItem('game_token');
          }
        }
      }
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setLoading(false);
    }
  };

  // Load manual characters from player data
  useEffect(() => {
    if (dashboard?.player?.manual_characters) {
      setManualCharacters(dashboard.player.manual_characters);
    }
  }, [dashboard]);

  // Save manual character for a month
  const saveManualCharacter = async (month, character) => {
    if (!isManualEntryPeriod) {
      toast.error('La saisie manuelle est disponible uniquement du 1er au 24 décembre');
      return;
    }
    
    setSavingCharacter(month);
    try {
      const token = localStorage.getItem('game_token');
      await api.post(`/game/players/me/manual-character?token=${token}`, {
        month,
        character: character.toUpperCase(),
        year: currentYear
      });
      setManualCharacters(prev => ({ ...prev, [month]: character.toUpperCase() }));
      toast.success(`Caractère "${character.toUpperCase()}" enregistré pour ${MONTHS[month - 1].fullName}`);
      
      // Refresh annual progress to update display_combination
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingCharacter(null);
    }
  };

  // Get character for a month (from quiz completion, secrets, or manual entry)
  const getCharacterForMonth = (month) => {
    const monthStr = String(month);
    
    // First, check if we have earned characters from annual progress
    if (annualProgress) {
      // Check if quiz character was earned for this month
      if (annualProgress.earned_quiz_chars && annualProgress.earned_quiz_chars[monthStr]) {
        return annualProgress.earned_quiz_chars[monthStr];
      }
      
      // Check if secret character was earned for this month
      if (annualProgress.earned_secret_chars && annualProgress.earned_secret_chars[monthStr]) {
        return annualProgress.earned_secret_chars[monthStr];
      }
    }
    
    // Fallback: check if player passed a quiz for this month (using local game data)
    const periodicGames = getPeriodicGames();
    const monthGame = periodicGames.find(g => g.month === month);
    
    if (monthGame && hasPassedGame(monthGame)) {
      // Try to get from earned_quiz_chars first
      if (annualProgress?.earned_quiz_chars?.[monthStr]) {
        return annualProgress.earned_quiz_chars[monthStr];
      }
      // Otherwise try reward_character
      return monthGame.reward_character || '✓';
    }
    
    // Finally, check for manual entry
    return manualCharacters[month] || null;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await api.post('/game/players/login', { email: loginEmail, password: loginPassword });
      localStorage.setItem('game_token', res.data.token);
      toast.success('Connexion réussie !');
      setIsAuthenticated(true);
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email ou mot de passe incorrect');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (!registerData.accepted_rules) {
      toast.error('Vous devez accepter la politique de confidentialité');
      return;
    }
    setRegisterLoading(true);
    try {
      // Register the user
      const registerRes = await api.post('/game/players/register', registerData);
      
      // If avatar selected, upload it after registration
      if (registerAvatar && registerRes.data?.player_id) {
        try {
          // Login first to get token
          const loginRes = await api.post('/game/players/login', {
            email: registerData.email,
            password: registerData.password
          });
          
          if (loginRes.data?.token) {
            // Upload avatar - use 'avatar' as field name (expected by backend)
            const formData = new FormData();
            formData.append('avatar', registerAvatar);
            await api.post(`/game/players/avatar?token=${loginRes.data.token}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          }
        } catch (avatarErr) {
          console.log('Avatar upload failed, but registration successful', avatarErr);
        }
      }
      
      toast.success('Inscription réussie ! Votre compte est en attente de validation.');
      setShowLogin(true);
      setLoginEmail(registerData.email);
      setRegisterAvatar(null);
      setRegisterAvatarPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setRegisterLoading(false);
    }
  };
  
  // Handle avatar selection during registration
  const handleRegisterAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, WebP ou GIF.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image trop grande. Maximum 2 Mo.');
      return;
    }
    
    setRegisterAvatar(file);
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setRegisterAvatarPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleLogout = () => {
    localStorage.removeItem('game_token');
    setIsAuthenticated(false);
    setPlayer(null);
    setDashboard(null);
    setPlayedGameIds([]);
  };

  const handleSavePseudo = async () => {
    const token = localStorage.getItem('game_token');
    if (!token) return;
    setSavingPseudo(true);
    try {
      await api.put(`/game/players/me/pseudo?token=${token}`, { pseudo: newPseudo });
      toast.success('Pseudo mis à jour !');
      setEditingPseudo(false);
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSavingPseudo(false);
    }
  };

  // Check if player completed a game and get score info
  const hasPlayedGame = (gameId) => playedGameIds.includes(gameId);
  
  // Get score info for a game (to check if perfect score)
  const getGameScoreInfo = (gameId) => {
    return dashboard?.recent_scores?.find(s => s.game_id === gameId);
  };
  
  // Check if player has passed a game (based on min_success_rate or annualProgress)
  const hasPassedGame = (game) => {
    if (!game) return false;
    
    // First check annualProgress for completed months
    if (game.game_type === 'periodique' && game.month && annualProgress) {
      const monthStr = String(game.month);
      // Check if character was earned for this month (quiz validated)
      if (annualProgress.earned_quiz_chars?.[monthStr]) {
        return true;
      }
      // Check completed_months array
      if (annualProgress.completed_months?.includes(game.month)) {
        return true;
      }
    }
    
    // Fallback to score check
    const scoreInfo = getGameScoreInfo(game.id);
    if (!scoreInfo) return false;
    const successRate = (scoreInfo.correct_answers / scoreInfo.total_questions) * 100;
    const minRate = game.min_success_rate || 50; // Default 50% to match quiz success threshold
    return successRate >= minRate;
  };
  
  // Legacy function for backward compatibility
  const hasPerfectScore = (gameId) => {
    const game = allGames.find(g => g.id === gameId);
    return hasPassedGame(game);
  };

  // Get periodic games for timeline (sorted by month)
  const getPeriodicGames = () => {
    let periodic = allGames.filter(g => g.game_type === 'periodique' && g.year === currentYear);
    if (periodic.length === 0) {
      const years = [...new Set(allGames.filter(g => g.game_type === 'periodique').map(g => g.year))].sort((a, b) => b - a);
      if (years.length > 0) {
        periodic = allGames.filter(g => g.game_type === 'periodique' && g.year === years[0]);
      }
    }
    // Sort by month to ensure correct order
    return periodic.sort((a, b) => (a.month || 0) - (b.month || 0));
  };

  // Get one-time games
  const getOneTimeGames = () => allGames.filter(g => g.game_type !== 'periodique' && !g.is_finished);

  // Get hint for a specific month from annual game data
  const getHintForMonth = (monthNum) => {
    if (!annualGame?.secret_hints) return null;
    return annualGame.secret_hints.find(h => h.month === monthNum);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/70">Chargement...</p>
        </div>
      </div>
    );
  }

  const periodicGames = getPeriodicGames();
  const oneTimeGames = getOneTimeGames();
  const timelineYear = periodicGames[0]?.year || currentYear;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 relative overflow-hidden">
      {/* Subtle animated background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-cyan-900/10 via-transparent to-transparent"></div>
      </div>
      
      {/* Hint Popup Modal */}
      <HintPopupModal 
        show={showHintPopup}
        monthNum={selectedHintMonth}
        hint={selectedHintData}
        onClose={closeHintPopup}
      />
      
      {/* Header - Mon Espace Joueur */}
      <header className="border-b border-blue-800/30 bg-slate-900/95 backdrop-blur-sm sticky top-0 z-50 relative">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/logos/aa9ace7f-4c0.webp`}
              alt="Agence MSC"
              className="h-10 object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
              }}
            />
            <div className="flex flex-col">
              <span className="font-bold text-white text-lg">Mon Espace Joueur</span>
              <span className="text-xs text-blue-400">Agence MSC - Jeux Concours</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="ghost" className="text-blue-400 hover:text-white hover:bg-blue-900/30">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au site
              </Button>
            </Link>
            {isAuthenticated && player && (
              <>
                <div className="text-right hidden sm:block">
                  <p className="text-white font-medium">{player.first_name}</p>
                  <p className="text-cyan-400 text-sm">{player.total_score} pts</p>
                </div>
                <Button variant="ghost" className="text-blue-400 hover:text-white hover:bg-blue-900/30" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 relative z-10">
        {/* Auth Section - Top */}
        {!isAuthenticated ? (
          <Card className="bg-gradient-to-br from-blue-950/60 to-slate-900/80 border-blue-500/30 mb-8 max-w-lg mx-auto shadow-xl shadow-blue-500/10">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-blue-500/40">
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-xl text-white">Mon Espace Joueur</CardTitle>
              <CardDescription className="text-blue-300/70">
                Connectez-vous pour accéder à vos jeux et classements
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {/* Tabs */}
              <div className="flex mb-6 border-b border-blue-500/30">
                <button
                  onClick={() => setShowLogin(true)}
                  className={`flex-1 py-3 text-center font-medium transition-colors ${showLogin ? 'text-white border-b-2 border-blue-500' : 'text-white/50'}`}
                >
                  <LogIn className="w-4 h-4 inline mr-2" />Connexion
                </button>
                <button
                  onClick={() => setShowLogin(false)}
                  className={`flex-1 py-3 text-center font-medium transition-colors ${!showLogin ? 'text-white border-b-2 border-blue-500' : 'text-white/50'}`}
                >
                  <UserPlus className="w-4 h-4 inline mr-2" />Inscription
                </button>
              </div>

              {showLogin ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white/80">Email</Label>
                    <Input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                      className="bg-slate-800/50 border-blue-500/30 text-white focus:border-blue-500" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/80">Mot de passe</Label>
                    <div className="relative">
                      <Input 
                        type={showLoginPassword ? "text" : "password"} 
                        value={loginPassword} 
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="bg-slate-800/50 border-blue-500/30 text-white focus:border-blue-500 pr-10" 
                        required 
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                        data-testid="toggle-login-password"
                      >
                        {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500" disabled={loginLoading}>
                    {loginLoading ? 'Connexion...' : 'Se connecter'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleRegister} className="space-y-3">
                  {/* Avatar Upload Section */}
                  <div className="flex justify-center mb-2">
                    <div className="relative">
                      {registerAvatarPreview ? (
                        <img 
                          src={registerAvatarPreview} 
                          alt="Avatar preview" 
                          className="w-20 h-20 rounded-full object-cover border-2 border-blue-500 shadow-lg"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center border-2 border-blue-500/50">
                          <User className="w-10 h-10 text-white/70" />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => registerAvatarInputRef.current?.click()}
                        className="absolute -bottom-1 -right-1 w-8 h-8 bg-blue-600 hover:bg-blue-500 rounded-full flex items-center justify-center text-white shadow-lg transition-all"
                        title="Ajouter un avatar"
                      >
                        <Camera className="w-4 h-4" />
                      </button>
                      <input
                        ref={registerAvatarInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleRegisterAvatarSelect}
                        className="hidden"
                      />
                    </div>
                  </div>
                  <p className="text-center text-xs text-white/50 mb-2">Photo de profil (optionnel)</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-white/80 text-sm">Prénom *</Label>
                      <Input value={registerData.first_name} onChange={(e) => setRegisterData({...registerData, first_name: e.target.value})}
                        className="bg-slate-800/50 border-blue-500/30 text-white focus:border-blue-500" required /></div>
                    <div><Label className="text-white/80 text-sm">Pseudo</Label>
                      <Input value={registerData.pseudo} onChange={(e) => setRegisterData({...registerData, pseudo: e.target.value})}
                        className="bg-slate-800/50 border-blue-500/30 text-white focus:border-blue-500" placeholder="Pour le classement" /></div>
                  </div>
                  <div><Label className="text-white/80 text-sm">Email *</Label>
                    <Input type="email" value={registerData.email} onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                      className="bg-slate-800/50 border-blue-500/30 text-white focus:border-blue-500" required /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-white/80 text-sm">Entreprise *</Label>
                      <Input value={registerData.company_name} onChange={(e) => setRegisterData({...registerData, company_name: e.target.value})}
                        className="bg-slate-800/50 border-blue-500/30 text-white focus:border-blue-500" required /></div>
                    <div><Label className="text-white/80 text-sm">Téléphone *</Label>
                      <Input value={registerData.phone} onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                        className="bg-slate-800/50 border-blue-500/30 text-white focus:border-blue-500" required /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white/80 text-sm">Mot de passe *</Label>
                      <div className="relative">
                        <Input 
                          type={showRegisterPassword ? "text" : "password"} 
                          value={registerData.password} 
                          onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                          className="bg-slate-800/50 border-blue-500/30 text-white focus:border-blue-500 pr-10" 
                          required 
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                          data-testid="toggle-register-password"
                        >
                          {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-white/80 text-sm">Confirmer *</Label>
                      <div className="relative">
                        <Input 
                          type={showRegisterConfirmPassword ? "text" : "password"} 
                          value={registerData.confirmPassword} 
                          onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                          className="bg-slate-800/50 border-blue-500/30 text-white focus:border-blue-500 pr-10" 
                          required 
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterConfirmPassword(!showRegisterConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                          data-testid="toggle-register-confirm-password"
                        >
                          {showRegisterConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <input type="checkbox" id="rules" checked={registerData.accepted_rules}
                      onChange={(e) => setRegisterData({...registerData, accepted_rules: e.target.checked})} className="mt-1 accent-blue-500" />
                    <Label htmlFor="rules" className="text-white/60 text-sm">J&apos;accepte la politique de confidentialité du site *</Label>
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500" disabled={registerLoading}>
                    {registerLoading ? 'Inscription...' : "S'inscrire"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Player Profile Card - Blue Theme (Independent from Coffre) */
          <Card className="bg-gradient-to-br from-blue-950/40 to-slate-900/80 border-blue-500/30 mb-8">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                {/* Avatar with upload option */}
                <div className="relative group">
                  {player?.avatar_url ? (
                    <img 
                      src={player.avatar_url} 
                      alt="Avatar" 
                      className="w-16 h-16 rounded-2xl object-cover shadow-lg shadow-blue-500/30"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-2xl font-bold text-white shadow-lg shadow-blue-500/30">
                      {player?.first_name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  {/* Camera button overlay */}
                  <button
                    onClick={() => avatarInputRef.current?.click()}
                    className="absolute -bottom-1 -right-1 w-7 h-7 bg-blue-600 hover:bg-blue-500 rounded-full flex items-center justify-center text-white shadow-lg transition-all opacity-80 group-hover:opacity-100"
                    title="Changer l'avatar"
                    data-testid="change-avatar-btn"
                  >
                    {uploadingAvatar ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                  </button>
                  {/* Hidden file input */}
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    data-testid="avatar-input"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h1 className="text-xl font-bold text-white">{player?.first_name}</h1>
                    {player?.pseudo && (
                      <Badge variant="outline" className="text-blue-400 border-blue-500/50">@{player.pseudo}</Badge>
                    )}
                  </div>
                  <p className="text-white/60 text-sm">{player?.company_name}</p>
                  {editingPseudo ? (
                    <div className="mt-2 flex items-center gap-2">
                      <Input value={newPseudo} onChange={(e) => setNewPseudo(e.target.value)}
                        className="w-64 bg-black/50 border-blue-500/30 text-white h-8" maxLength={20} placeholder="Votre pseudo" />
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleSavePseudo} disabled={savingPseudo}>OK</Button>
                      <Button size="sm" variant="ghost" className="text-white/60" onClick={() => setEditingPseudo(false)}>✕</Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="ghost" className="mt-1 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 h-7 px-2" onClick={() => setEditingPseudo(true)}>
                      <Edit className="w-3 h-3 mr-1" />{player?.pseudo ? 'Modifier pseudo' : 'Choisir un pseudo'}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <Trophy className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                    <p className="text-xl font-bold text-white">{player?.total_score || 0}</p>
                    <p className="text-xs text-white/50">Points</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <Medal className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                    <p className="text-xl font-bold text-white">#{dashboard?.player?.global_rank || '-'}</p>
                    <p className="text-xs text-white/50">Rang</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <Gamepad2 className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                    <p className="text-xl font-bold text-white">{player?.games_played || 0}</p>
                    <p className="text-xs text-white/50">Parties</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ============================================ */}
        {/* RÉSUMÉ DE PROGRESSION - Actions rapides */}
        {/* ============================================ */}
        {/* Section Ma Progression - Thème Rouge (lié au Coffre MSC) */}
        {/* ============================================ */}
        {isAuthenticated && periodicGames.length > 0 && (
          <div className="mb-8">
            <Card className="bg-gradient-to-r from-red-950/50 via-black/80 to-red-950/50 border border-red-500/40 shadow-xl">
              <CardContent className="p-6">
                {/* Titre de la section */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Target className="w-6 h-6 text-red-400" />
                    Ma Progression - Le Coffre MSC
                  </h2>
                  {dashboard?.player?.annual_rank && (
                    <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-lg px-4 py-2">
                      <Trophy className="w-5 h-5 mr-2" />
                      #{dashboard.player.annual_rank} au classement
                    </Badge>
                  )}
                </div>

                {/* Stats rapides */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {(() => {
                    // Calculate stats
                    const totalMonthlyQuizzes = periodicGames.length;
                    const playedQuizzes = periodicGames.filter(g => hasPlayedGame(g.id)).length;
                    const validatedQuizzes = periodicGames.filter(g => hasPassedGame(g)).length;
                    const availableQuizzes = periodicGames.filter(g => g.is_active && !g.is_finished && !hasPassedGame(g)).length;
                    
                    return (
                      <>
                        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                          <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-green-400">{validatedQuizzes}</p>
                          <p className="text-xs text-white/60">Quiz validés</p>
                        </div>
                        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                          <Play className="w-8 h-8 text-red-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-red-400">{availableQuizzes}</p>
                          <p className="text-xs text-white/60">Quiz disponibles</p>
                        </div>
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-center">
                          <Trophy className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-yellow-400">{player?.total_score || 0}</p>
                          <p className="text-xs text-white/60">Points total</p>
                        </div>
                        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                          <Gift className="w-8 h-8 text-red-400 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-red-400">{annualProgress?.characters_earned || 0}/24</p>
                          <p className="text-xs text-white/60">Caractères</p>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Quiz à jouer maintenant */}
                {(() => {
                  // Sort games by month to find the correct next quiz
                  const sortedGames = [...periodicGames].sort((a, b) => (a.month || 0) - (b.month || 0));
                  
                  // Find current month quiz (playable now)
                  const currentMonthQuiz = sortedGames.find(g => 
                    g.month === currentMonth && g.is_active && !g.is_finished && !hasPassedGame(g)
                  );
                  
                  // Find past months quizzes that can be retried (rattrapage)
                  const pastRetryQuiz = sortedGames.find(g => 
                    g.month < currentMonth && g.is_active && !g.is_finished && !hasPassedGame(g)
                  );
                  
                  // Find next future quiz (not yet available)
                  const nextFutureQuiz = sortedGames.find(g => 
                    g.month > currentMonth && g.is_active && !g.is_finished
                  );
                  
                  // Priority: current month > past retry > future quiz info
                  const quizToPlay = currentMonthQuiz || pastRetryQuiz;
                  const isRetry = !!pastRetryQuiz && !currentMonthQuiz;
                  
                  if (quizToPlay) {
                    const monthName = MONTHS.find(m => m.num === quizToPlay.month)?.fullName || '';
                    
                    return (
                      <div className="bg-gradient-to-r from-red-600/20 via-red-700/20 to-red-600/20 border-2 border-red-500/50 rounded-xl p-4">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-xl ${isRetry ? 'bg-orange-500/30' : 'bg-red-500/30'} flex items-center justify-center`}>
                              {isRetry ? (
                                <RefreshCw className="w-7 h-7 text-orange-400" />
                              ) : (
                                <Play className="w-7 h-7 text-red-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-white/60 text-sm">
                                {isRetry ? '🔄 Quiz en rattrapage' : '🎮 Quiz du mois à jouer'}
                              </p>
                              <p className="text-white font-bold text-lg">
                                Quiz {monthName} {quizToPlay.year}
                              </p>
                              <p className="text-white/50 text-xs">
                                {isRetry ? 'Obtenez 50% pour valider le caractère' : 'Gagnez le caractère du mois !'}
                              </p>
                            </div>
                          </div>
                          <Link to={`/jeu/${quizToPlay.id}/jouer`}>
                            <Button size="lg" className={`${isRetry ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700' : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'} shadow-lg px-8 py-6 text-lg font-bold animate-pulse`}>
                              {isRetry ? (
                                <>
                                  <RefreshCw className="w-5 h-5 mr-2" />
                                  RATTRAPER LE QUIZ
                                </>
                              ) : (
                                <>
                                  <Play className="w-5 h-5 mr-2" />
                                  JOUER MAINTENANT
                                </>
                              )}
                              <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    );
                  } else if (nextFutureQuiz) {
                    // Show upcoming quiz info
                    const nextMonthName = MONTHS.find(m => m.num === nextFutureQuiz.month)?.fullName || '';
                    const nextMonthDate = new Date(nextFutureQuiz.year, nextFutureQuiz.month - 1, 1);
                    const availableDate = nextMonthDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
                    
                    return (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-xl bg-blue-500/30 flex items-center justify-center">
                              <Calendar className="w-7 h-7 text-blue-400" />
                            </div>
                            <div>
                              <p className="text-white/60 text-sm">📅 Prochain quiz</p>
                              <p className="text-white font-bold text-lg">
                                Quiz {nextMonthName} {nextFutureQuiz.year}
                              </p>
                              <p className="text-blue-400 text-sm font-medium">
                                Disponible le {availableDate}
                              </p>
                            </div>
                          </div>
                          <div className="text-center">
                            <p className="text-white/50 text-sm">Tous les quiz du mois sont validés !</p>
                            <p className="text-green-400 text-xs mt-1">✓ Bravo, revenez bientôt !</p>
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    // All quizzes completed
                    return (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                        <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
                        <p className="text-green-400 font-bold">Tous les quiz de l&apos;année sont validés !</p>
                        <p className="text-white/60 text-sm">Félicitations, vous avez complété tous les quiz.</p>
                      </div>
                    );
                  }
                })()}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ============================================ */}
        {/* SECTION 2 COLONNES: JEUX PONCTUELS | COFFRE MSC */}
        {/* ============================================ */}
        {isAuthenticated && (
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* COLONNE GAUCHE - JEUX PONCTUELS */}
            <div>
              <Card className="bg-gradient-to-br from-cyan-950/30 via-black/80 to-cyan-950/30 border border-cyan-500/40 shadow-xl h-full">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Gamepad2 className="w-6 h-6 text-cyan-400" />
                    Jeux Ponctuels
                  </h2>
                  
                  {oneTimeGames.filter(g => g.id !== mainGame?.id).length === 0 ? (
                    <div className="text-center py-8 text-white/60">
                      <Gamepad2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Aucun jeu ponctuel disponible</p>
                      <p className="text-sm">De nouveaux jeux arrivent bientôt !</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {oneTimeGames.filter(g => g.id !== mainGame?.id).map(game => {
                        const played = hasPlayedGame(game.id);
                        return (
                          <Link key={game.id} to={`/jeu/${game.id}`}>
                            <div className={`p-4 rounded-xl border transition-all hover:scale-[1.02] ${
                              played 
                                ? 'bg-green-500/10 border-green-500/30' 
                                : 'bg-cyan-500/10 border-cyan-500/30 hover:border-cyan-400/50'
                            }`}>
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  played ? 'bg-green-500/20' : 'bg-cyan-500/20'
                                }`}>
                                  {played ? <CheckCircle className="w-5 h-5 text-green-400" /> : <Play className="w-5 h-5 text-cyan-400" />}
                                </div>
                                <div className="flex-1">
                                  <h3 className="font-semibold text-white text-sm">{game.title}</h3>
                                  <p className="text-cyan-300 text-xs flex items-center gap-1">
                                    <Gift className="w-3 h-3" />{game.prize}
                                  </p>
                                </div>
                                {played ? (
                                  <Badge className="bg-green-500/20 text-green-400 text-xs">✓ Joué</Badge>
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-cyan-400" />
                                )}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* COLONNE DROITE - LE COFFRE MSC (Résumé) */}
            <div>
              <Card className="bg-gradient-to-br from-red-950/30 via-black/80 to-red-950/30 border border-red-500/40 shadow-xl h-full">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                      <Lock className="w-6 h-6 text-red-400" />
                      Le Coffre MSC {currentYear}
                    </h2>
                  </div>
                  
                  {/* Gros lot */}
                  {annualGame && (
                    <div className="bg-gradient-to-r from-red-600/20 to-red-900/20 rounded-xl p-4 mb-4 border border-red-500/30">
                      <p className="text-red-400 text-xs font-semibold mb-1">🎁 Gros lot à gagner</p>
                      <p className="text-white font-bold text-sm">{annualGame.prize || 'À découvrir'}</p>
                    </div>
                  )}
                  
                  {/* Stats rapides */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="text-center p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <p className="text-2xl font-bold text-red-400">{periodicGames.filter(g => hasPassedGame(g)).length}/12</p>
                      <p className="text-xs text-white/50">Quiz validés</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-white/5 border border-white/10">
                      <p className="text-2xl font-bold text-white">{Object.keys(annualProgress?.earned_secret_chars || {}).length}/12</p>
                      <p className="text-xs text-white/50">Indices trouvés</p>
                    </div>
                  </div>
                  
                  {/* Progression barre */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-white/60">Combinaison</span>
                      <span className="text-green-400 font-bold">{annualProgress?.characters_earned || 0}/24</span>
                    </div>
                    <div className="h-2 bg-red-950 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all"
                        style={{ width: `${((annualProgress?.characters_earned || 0) / 24) * 100}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* CTA */}
                  <Link to="/ma-progression-coffre" className="block">
                    <Button 
                      className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600"
                      data-testid="open-coffre-detail-btn"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Voir ma Progression
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* CLASSEMENT GÉNÉRAL ANNUEL */}
        {/* ============================================ */}
        {isAuthenticated && (
          <div className="mb-8">
            {/* Explication du classement */}
            <div className="mb-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
              <p className="text-sm text-center text-white/80">
                <Trophy className="w-4 h-4 inline text-yellow-400 mr-1" />
                <strong className="text-yellow-400">Classement annuel :</strong> Vos points s&apos;accumulent sur tous les quiz jusqu&apos;au 31 décembre.
                <span className="text-white/60 ml-1">Les meilleurs joueurs remportent des lots selon leur classement !</span>
              </p>
            </div>
            
            <Card className="bg-gradient-to-br from-yellow-950/30 via-black/80 to-yellow-950/30 border border-yellow-500/40 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-yellow-400" />
                    Classement Annuel {currentYear}
                  </h2>
                  <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/40">
                    Top 20
                  </Badge>
                </div>
                
                {loadingLeaderboard ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
                  </div>
                ) : annualLeaderboard.length === 0 ? (
                  <div className="text-center py-8 text-white/60">
                    <Trophy className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Aucun joueur dans le classement pour le moment</p>
                    <p className="text-sm">Participez aux quiz pour apparaître ici !</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {annualLeaderboard.map((entry, idx) => {
                      const isCurrentPlayer = player?.id === entry.player_id;
                      const rankColors = {
                        0: 'bg-gradient-to-r from-yellow-500 to-amber-500 text-black',
                        1: 'bg-gradient-to-r from-gray-300 to-gray-400 text-black',
                        2: 'bg-gradient-to-r from-orange-600 to-orange-700 text-white'
                      };
                      
                      return (
                        <div 
                          key={entry.player_id || idx}
                          className={`flex items-center gap-4 p-3 rounded-lg ${
                            isCurrentPlayer 
                              ? 'bg-cyan-500/20 border-2 border-cyan-500/50' 
                              : 'bg-white/5 hover:bg-white/10'
                          } transition-colors`}
                        >
                          {/* Rank */}
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                            rankColors[idx] || 'bg-white/10 text-white'
                          }`}>
                            {idx + 1}
                          </div>
                          
                          {/* Avatar */}
                          {entry.avatar_url ? (
                            <img 
                              src={entry.avatar_url} 
                              alt=""
                              className="w-10 h-10 rounded-full object-cover border-2 border-white/20"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-sm font-bold text-white">
                              {(entry.display_name || entry.first_name || 'J')?.[0]?.toUpperCase()}
                            </div>
                          )}
                          
                          {/* Player info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`font-semibold ${isCurrentPlayer ? 'text-cyan-400' : 'text-white'}`}>
                                {entry.display_name || entry.first_name || 'Joueur'}
                              </span>
                              {isCurrentPlayer && (
                                <Badge className="bg-cyan-500/30 text-cyan-300 text-xs">Vous</Badge>
                              )}
                            </div>
                            <p className="text-xs text-white/50">{entry.games_played || 0} quiz joués</p>
                          </div>
                          
                          {/* Score */}
                          <div className="text-right">
                            <p className={`font-bold ${idx < 3 ? 'text-yellow-400' : 'text-white'}`}>
                              {entry.total_score || 0} pts
                            </p>
                            {entry.characters_count > 0 && (
                              <p className="text-xs text-green-400">{entry.characters_count} caractères</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Position du joueur si hors top 20 */}
                {isAuthenticated && player && dashboard?.player?.annual_rank > 20 && (
                  <div className="mt-4 pt-4 border-t border-yellow-500/30">
                    <p className="text-center text-white/60 text-sm">
                      Votre position : <span className="text-yellow-400 font-bold">#{dashboard.player.annual_rank}</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Player Results */}
        {isAuthenticated && dashboard?.recent_scores?.length > 0 && (
          <Card className="bg-gradient-to-br from-blue-950/30 to-slate-900/60 border-blue-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Star className="w-5 h-5 text-cyan-400" />
                Mes résultats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard.recent_scores.map((score, idx) => (
                  <div key={score.id || idx} className="flex items-center justify-between p-4 rounded-xl bg-slate-800/40 border border-blue-500/20">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        score.correct_answers === score.total_questions ? 'bg-green-500/20 text-green-400' :
                        score.correct_answers >= score.total_questions / 2 ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/10 text-white/50'
                      }`}>
                        <Target className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{score.game_title}</p>
                        <p className="text-sm text-white/50">{score.correct_answers}/{score.total_questions} bonnes réponses</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-cyan-400">{score.score} pts</p>
                      <p className="text-xs text-white/40">{new Date(score.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-blue-800/30 mt-12 relative z-10">
        <div className="max-w-6xl mx-auto text-center text-white/40 text-sm">
          <p>© {new Date().getFullYear()} MSC L&apos;Agence - Espace Joueur</p>
        </div>
      </footer>
    </div>
  );
};

export default PlayerDashboardPage;
